import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, delay, finalize, Observable, throwError } from 'rxjs';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { EventService } from '../services/event.service';
import { EventTypes } from 'src/app/shared/enum/event.types';
import { GlobalErrorEventModel } from 'src/app/shared/models/global-error-event.model';
import { Router } from '@angular/router';
import { AuthenticationService } from '@dop/authentication';

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerInterceptor implements HttpInterceptor {
  private readonly maxRetries = 2;
  private readonly delayms = 100;

  constructor(
    private readonly eventService: EventService,
    private readonly loaderService: LoaderService,
    private readonly router: Router,
    private readonly authenticationService: AuthenticationService
  ) {}

  public intercept(request: HttpRequest<void>, next: HttpHandler): Observable<HttpEvent<void>> {
    if (!request.url.includes('bff/session/state')) {
      this.showLoading();
    }
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        this.handleRedirection(error.status, request.url);
        if (this.isTimeoutError(error)) {
          this.showLoading();
          return this.handleRetry(request, next, error, 0); // Implement retry logic here
        } else if (error.status >= 500 && !request.url.includes('bff/translations')) {
          const globalErrorModel = new GlobalErrorEventModel(error.status, false, error.error?.reference);
          this.eventService.broadCast(EventTypes.GlobalServiceError, globalErrorModel);
          this.hideLoading(); // Hide spinner on error
          return throwError(() => error);
        } else {
          // If some other error, return back it to the request source
          this.hideLoading(); // Hide spinner on error
          return throwError(() => error);
        }
      }),
      finalize(() => {
        //For excluding state calls
        if (!request.url.includes('bff/session/state')) {
          this.hideLoading(); // Hide spinner when request completes
        }
      })
    );
  }

  private handleRedirection(statusCode: number, url: string): void {
    if (statusCode === 401 && url.includes('bff/dol/userinfo')) {
      this.authenticationService.login('/').then(() => false);
    } else if (
      statusCode >= 500 &&
      (url.includes('bff/session/state') || url.includes('bff/dol/userinfo') || url.includes('bff/authn/login'))
    ) {
      this.router.navigate(['/error']);
    }
  }

  private showLoading(): void {
    this.loaderService.show();
  }

  private hideLoading(): void {
    this.loaderService.hide();
  }

  private isTimeoutError(error: HttpErrorResponse): boolean {
    return error.status >= 500 && error.statusText.includes('Timeout');
  }

  private readonly handleRetry = (
    request: HttpRequest<void>,
    next: HttpHandler,
    previousError: HttpErrorResponse,
    retries: number
  ): Observable<HttpEvent<any>> => {
    this.showLoading();
    if (retries >= this.maxRetries) {
      this.hideLoading(); // Hide spinner when retries are exhausted
      const globalErrorModel = new GlobalErrorEventModel(previousError.status, true, previousError.error?.reference);
      this.eventService.broadCast(EventTypes.GlobalServiceError, globalErrorModel);
      return throwError(() => previousError);
    }

    return next.handle(request).pipe(
      delay(this.delayms),
      catchError((error: HttpErrorResponse) => {
        if (this.isTimeoutError(error)) {
          return this.handleRetry(request, next, error, retries + 1);
        } else {
          this.hideLoading(); // Hide spinner on error
          return throwError(() => error);
        }
      })
    );
  };
}
